import { useEffect } from 'react';

import { useLocation, useParams } from 'react-router';

import { useQuery } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { getQuizDetailsOptions } from '@cfa/pathway-fe-shared/xp';

import { useFlags } from '@/hooks/useFlags';

import { withRoles } from '@/utils/withRoles';

import GenericError from 'components/Error/GenericError';
import LoadingOverlay from 'components/LoadingOverlay/LoadingOverlay';
import ToastMessageBlock from 'components/Toasts/SuccessToast';

import BuildQuizzesView from './BuildQuizzesView';
import BuildQuizzesViewV2 from './BuildQuizzesViewV2';

const CREATE_ROUTE_QUIZ_ID = 'new';

interface EditorLocationState {
  quizName?: string;
}

/**
 * Picks V1 vs V2 for the shared `/plans/manage-quizzes/:quizId` route.
 * New V2 create flows require the `v2Quizzes` LaunchDarkly flag; existing
 * quizzes with `v2: true` always use the V2 editor.
 *
 * While the discriminator query is in flight, show a loading overlay instead of
 * mounting Classic with an empty DEFAULT_QUIZ — that briefly enabled V1 rename
 * validation (Invalid Quiz Name) on V2 routes. Use `isLoading` (pending +
 * fetching) so a stuck non-fetching query still falls through to Classic.
 */
const BuildQuizzesViewGate = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const location = useLocation();
  const state = location.state as EditorLocationState | null;
  const { v2Quizzes } = useFlags();
  const { t } = useTranslation();

  const isCreateRoute = !quizId || quizId === CREATE_ROUTE_QUIZ_ID;

  // V2 create session after first POST (URL is the real id; state still has quizName).
  const isV2CreateSession =
    v2Quizzes &&
    !isCreateRoute &&
    typeof state?.quizName === 'string' &&
    state.quizName.trim() !== '';

  const shouldFetchQuizDiscriminator = !isCreateRoute && !isV2CreateSession;

  const {
    data: quizData,
    isLoading: isLoadingQuiz,
    isError,
  } = useQuery({
    ...getQuizDetailsOptions({
      path: {
        quizId: quizId ?? '',
      },
    }),
    enabled: shouldFetchQuizDiscriminator,
  });

  // Observes the shared cache entry, so this also fires when V2's fetch fails.
  useEffect(() => {
    if (!isError) {
      return;
    }
    toast.custom(
      toastObj => (
        <ToastMessageBlock id={toastObj.id} severity="error">
          {t('TrainingPlans.toastMessage.errorLoadingQuiz')}
        </ToastMessageBlock>
      ),
      { id: `quiz-load-error-${quizId}` },
    );
  }, [isError, quizId, t]);

  // No isError check: V2 shares this query key, so its failures would bounce
  // the editor to Classic and back in a refetch loop. V2 handles its own errors.
  const useV2CreateFlow = v2Quizzes && (isCreateRoute || isV2CreateSession);
  const useV2ExistingQuiz = quizData?.v2 === true;

  if (useV2CreateFlow || useV2ExistingQuiz) {
    return <BuildQuizzesViewV2 />;
  }

  if (shouldFetchQuizDiscriminator && isLoadingQuiz) {
    return <LoadingOverlay isOpen fixed />;
  }

  // Falling through to Classic remounts its same-key query, which refetches the
  // errored query and flips this gate back to loading — an infinite loop.
  if (shouldFetchQuizDiscriminator && isError) {
    return <GenericError />;
  }

  return <BuildQuizzesView />;
};

const BuildQuizzesViewGateWithRoles = withRoles(
  BuildQuizzesViewGate,
  'quizzes.create',
);

export default BuildQuizzesViewGateWithRoles;
